import { getSubscriptionStatus, checkAccess } from '@/api/subscription';

export default {
  namespaced: true,
  
  state: {
    status: null,
    access: null,
    loading: false,
    loaded: false,
  },
  
  getters: {
    isPaid(state, getters, rootState) {
      if (rootState.auth.user?.role === 'admin') return true;
      const paidTypes = ['paid', 'base', 'premium'];
      return paidTypes.includes(state.status?.subscription_type) && state.status?.is_active;
    },
    isBase(state, getters, rootState) {
      if (rootState.auth.user?.role === 'admin') return false;
      const type = state.status?.subscription_type;
      const plan = state.status?.subscription_plan;
      const isActive = state.status?.is_active;
      if (!isActive) return false;
      if (type === 'base') return true;
      if (type === 'paid' && plan === 'base') return true;
      return false;
    },
    isPremium(state, getters, rootState) {
      if (rootState.auth.user?.role === 'admin') return true;
      const type = state.status?.subscription_type;
      const plan = state.status?.subscription_plan;
      const isActive = state.status?.is_active;
      if (!isActive) return false;
      if (type === 'premium') return true;
      if (type === 'paid' && plan !== 'base') return true;
      return false;
    },
    canBuyCredits(state, getters) {
      return getters.isPremium;
    },
    isAdmin(state, getters, rootState) {
      return rootState.auth.user?.role === 'admin';
    },
    subscriptionType(state) {
      return state.status?.subscription_type || 'free';
    },
    subscriptionEndDate(state) {
      return state.status?.subscription_end_date;
    },
    subscriptionPlan(state) {
      return state.status?.subscription_plan;
    },
    aiCredits(state) {
      return state.status?.ai_credits || 0;
    },
    canExportPdf(state, getters) {
      return getters.isPremium;
    },
    canExportExcel(state, getters) {
      return getters.isPremium;
    },
    canAddElements(state, getters) {
      return getters.isPaid;
    },
    canUploadFonts(state, getters) {
      return getters.isPaid;
    },
    canEditPrices(state, getters) {
      return getters.isPaid;
    },
    canSeePrices(state, getters) {
      return getters.isPaid;
    },
    canUseCalculator(state, getters) {
      return getters.isPremium;
    },
    hasWatermark(state, getters) {
      return !getters.isPaid;
    },
    accessFeatures(state) {
      return state.access?.features || {
        canExportPdf: false,
        canExportExcel: false,
        canAddElements: false,
        canUploadFonts: false,
        canEditPrices: false,
        canSeePrices: false,
        canUseCalculator: false,
        hasWatermark: true
      };
    }
  },
  
  mutations: {
    SET_STATUS(state, status) {
      state.status = status;
    },
    SET_ACCESS(state, access) {
      state.access = access;
    },
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_LOADED(state, loaded) {
      state.loaded = loaded;
    },
    CLEAR_SUBSCRIPTION(state) {
      state.status = null;
      state.access = null;
      state.loaded = false;
    }
  },
  
  actions: {
    async fetchStatus({ commit, rootState }) {
      if (!rootState.auth.isAuthenticated) return;
      
      commit('SET_LOADING', true);
      try {
        const status = await getSubscriptionStatus();
        commit('SET_STATUS', status);
        commit('SET_LOADED', true);
      } catch (error) {
        console.error('Error fetching subscription status:', error);
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    async fetchAccess({ commit, rootState }) {
      if (!rootState.auth.isAuthenticated) return;
      
      try {
        const access = await checkAccess();
        commit('SET_ACCESS', access);
      } catch (error) {
        console.error('Error fetching subscription access:', error);
      }
    },
    
    async init({ dispatch, state }) {
      if (!state.loaded) {
        await Promise.all([
          dispatch('fetchStatus'),
          dispatch('fetchAccess')
        ]);
      }
    },
    
    async refresh({ dispatch }) {
      await Promise.all([
        dispatch('fetchStatus'),
        dispatch('fetchAccess')
      ]);
    },
    
    clear({ commit }) {
      commit('CLEAR_SUBSCRIPTION');
    }
  }
};
